import React, { useMemo, useState } from 'react';
import { CornerDownRight, ListChecks, Repeat, Search } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { TYPES, STATUSES, PRIORITIES, DEPARTMENTS, TYPE_MAP, STATUS_MAP, PRIORITY_MAP, DEPARTMENT_MAP, RECURRENCE_MAP, isRecurring } from '../lib/constants';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { formatDueDate, dueState } from '../lib/dates';
import { cn } from '../lib/utils';
import UserAvatar from '../components/UserAvatar';

const DUE_TEXT = {
  overdue: 'text-red-600 dark:text-red-400 font-medium',
  today: 'text-amber-600 dark:text-amber-400 font-medium',
  soon: 'text-foreground',
  future: 'text-muted-foreground',
  none: 'text-muted-foreground',
};

const EMPTY_FILTERS = { search: '', type: '', status: '', priority: '', department: '', requestor: '', assignee: '', repeats: '' };

export default function TicketsPage() {
  const { visibleTickets, members, openEditTicket, loading, subtaskStats } = useTickets();
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const hasFilters = Object.values(filters).some(Boolean);

  const rows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return visibleTickets
      .filter((t) => {
        if (filters.type && t.type !== filters.type) return false;
        if (filters.status && t.status !== filters.status) return false;
        if (filters.priority && t.priority !== filters.priority) return false;
        if (filters.department && t.department !== filters.department) return false;
        if (filters.requestor && String(t.requestor?.id) !== filters.requestor) return false;
        if (filters.assignee === 'unassigned' && t.assignees?.length) return false;
        if (filters.assignee && filters.assignee !== 'unassigned' && !t.assignees?.some((a) => String(a.id) === filters.assignee)) return false;
        if (filters.repeats === 'recurring' && !isRecurring(t)) return false;
        if (filters.repeats === 'once' && isRecurring(t)) return false;
        if (q) {
          const hay = `${t.title} ${t.description} ${t.requestor?.name || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [visibleTickets, filters]);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filters.search} onChange={set('search')} placeholder="Search title, description, requestor…" className="pl-8" />
        </div>
        <Select value={filters.type} onChange={set('type')} className="w-auto">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </Select>
        <Select value={filters.status} onChange={set('status')} className="w-auto">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </Select>
        <Select value={filters.priority} onChange={set('priority')} className="w-auto">
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </Select>
        <Select value={filters.department} onChange={set('department')} className="w-auto">
          <option value="">All departments</option>
          {DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </Select>
        <Select value={filters.requestor} onChange={set('requestor')} className="w-auto">
          <option value="">All requestors</option>
          {members.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
        </Select>
        <Select value={filters.assignee} onChange={set('assignee')} className="w-auto">
          <option value="">All assignees</option>
          <option value="unassigned">Unassigned</option>
          {members.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
        </Select>
        <Select value={filters.repeats} onChange={set('repeats')} className="w-auto">
          <option value="">Repeating or not</option>
          <option value="recurring">Recurring only</option>
          <option value="once">One-off only</option>
        </Select>
        {hasFilters ? (
          <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>Clear</Button>
        ) : null}
      </div>

      <p className="mb-2 text-xs text-muted-foreground">{rows.length} ticket{rows.length === 1 ? '' : 's'}</p>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">title</th>
              <th className="px-3 py-2 font-medium">type</th>
              <th className="px-3 py-2 font-medium">dept</th>
              <th className="px-3 py-2 font-medium">status</th>
              <th className="px-3 py-2 font-medium">priority</th>
              <th className="px-3 py-2 font-medium">requestor</th>
              <th className="px-3 py-2 font-medium">assignee</th>
              <th className="px-3 py-2 font-medium">due</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const type = TYPE_MAP[t.type];
              const status = STATUS_MAP[t.status];
              const priority = PRIORITY_MAP[t.priority];
              const ds = t.status === 'done' ? 'future' : dueState(t.due_date);
              return (
                <tr
                  key={t.id}
                  onClick={() => openEditTicket(t)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-accent/50"
                >
                  <td className="max-w-[40rem] px-3 py-2.5">
                    <span className="flex min-w-0 items-center gap-1.5">
                      {t.parent ? (
                        <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground" title={`subtask of #${t.parent.id} ${t.parent.title}`}>
                          <CornerDownRight className="h-3 w-3" />#{t.parent.id}
                        </span>
                      ) : null}
                      <span className={cn('truncate font-medium', t.status === 'done' && 'text-muted-foreground line-through')}>
                        {t.title}
                      </span>
                      {subtaskStats[t.id]?.total ? (
                        <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground" title={`${subtaskStats[t.id].done} of ${subtaskStats[t.id].total} subtasks done`}>
                          <ListChecks className="h-3 w-3" />{subtaskStats[t.id].done}/{subtaskStats[t.id].total}
                        </span>
                      ) : null}
                      {isRecurring(t) ? (
                        <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" title={`repeats: ${RECURRENCE_MAP[t.recurrence]?.label}`} />
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><Badge className={type?.badge}>{type?.key}</Badge></td>
                  <td className="px-3 py-2.5"><Badge className={DEPARTMENT_MAP[t.department]?.badge}>{DEPARTMENT_MAP[t.department]?.label}</Badge></td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
                      <span className={cn('h-2 w-2 rounded-full', status?.dot)} />
                      {status?.key}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
                      <span className={cn('h-2 w-2 rounded-full', priority?.dot)} />
                      {priority?.key}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{t.requestor?.name || '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {t.assignees?.length ? (
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {t.assignees.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 whitespace-nowrap">
                            <UserAvatar user={a} className="h-4 w-4 rounded-sm" /> {a.name.split(' ')[0]}
                          </span>
                        ))}
                      </span>
                    ) : '—'}
                  </td>
                  <td className={cn('whitespace-nowrap px-3 py-2.5 text-xs', DUE_TEXT[ds])}>
                    {t.due_date ? formatDueDate(t.due_date) : '—'}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-sm text-muted-foreground">No tickets match these filters.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
